import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Typography, Box, Collapse, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { useQuery } from '@tanstack/react-query';
import { PublicKey } from '@solana/web3.js';
import { useTokenAccounts } from '../utils/tokens/hooks';
import { BTCI_PROGRAM_ID, USDT_PROGRAM_ID, TOKEN_PROGRAM_ID, WRAPPED_DOMI_MINT, WUSDT_MINT } from '../utils/tokens/instructions';
import { useBalanceInfo } from '../utils/wallet';
import { useWalletPublicKeys } from '../utils/wallet';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(1),
  },
  totalValue: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  expandButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(1),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5),
  },
  breakdown: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    width: '100%',
    '& > *': {
      margin: theme.spacing(0.25, 0),
    },
  },
  breakdownItem: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  staleIndicator: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
    opacity: 0.7,
    cursor: 'default',
  },
  staleIcon: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
  },
}));

interface TotalAccountValueProps {
  wallet: any;
}

interface Asset {
  name: string;
  balance: number;
  value: number;
  priceAvailable: boolean;
}

interface TokenConfig {
  _id?: string;
  symbol: string;
  name: string;
  publicKey: string;
  exchangeRate: number;
  decimals: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// API to fetch supported tokens from marketplace
const API_BASE_URL = 'https://airdrop2.domichain.io/v1/exchange';

// Abort each attempt after this long so
// react-query can fall back to the cached (persisted) value quickly.
const FETCH_TIMEOUT_MS = 10 * 1000;

// If the newest price data we hold is older than this, surface the subtle
// "as of <time>" indicator even if a fetch hasn't formally errored yet.
const PRICE_STALE_AFTER_MS = 10 * 60 * 1000;

const fetchWithTimeout = async (
  url: string,
  options: RequestInit = {},
): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

const fetchSupportedTokens = async (): Promise<TokenConfig[]> => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/getTokens`);
  if (!response.ok) {
    throw new Error('Failed to fetch supported tokens');
  }
  
  const responseData = await response.json();
  
  if (responseData.status !== 'success' || !responseData.data.success) {
    throw new Error(responseData.data?.error || 'Failed to fetch tokens');
  }
  
  return responseData.data.tokens || [];
};

// Custom hook to fetch DOMI price in USDT
function useDomiPrice() {
  return useQuery({
    queryKey: ['domi-usdt-price'],
    queryFn: async () => {
      try {
        const response = await fetchWithTimeout(`https://airdrop2.domichain.io/v1/exchange/price`, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          console.error('DOMI price fetch failed:', response.status, response.statusText);
          throw new Error('Failed to fetch DOMI price');
        }

        const data = await response.json();
        
        if (data.status !== 'success' || !data.data.success) {
          console.error('DOMI price API error:', data.error);
          throw new Error(data.error || 'Failed to fetch DOMI price');
        }

        return data.data.price; // Price of 1 DOMI in USDT
      } catch (error) {
        console.error('DOMI price fetch error:', error);
        throw error;
      }
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 25000, // Consider data stale after 25 seconds
    // Keep the last successful price around indefinitely=
    cacheTime: Infinity,
    retry: 2,
    meta: {
      persist: true,
    },
  });
}

// Custom hook to fetch BTC price in USD (treating as USDT equivalent)
function useBtcPrice() {
  return useQuery({
    queryKey: ['btc-usdt-price'],
    queryFn: async () => {
      try {
        const response = await fetchWithTimeout('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd');
        
        if (!response.ok) {
          console.error('BTC price fetch failed:', response.status, response.statusText);
          throw new Error('Failed to fetch BTC price');
        }
        
        const data = await response.json();
        
        if (!data.bitcoin || typeof data.bitcoin.usd !== 'number') {
          console.error('Invalid BTC price response structure:', data);
          throw new Error('Invalid BTC price response');
        }
        
        return data.bitcoin.usd; // Using USD as USDT equivalent
      } catch (error) {
        console.error('BTC price fetch error:', error);
        throw error;
      }
    },
    refetchInterval: 60000, // Refetch every minute
    staleTime: 50000, // Consider data stale after 50 seconds
    cacheTime: Infinity,
    retry: 2,
    meta: {
      persist: true,
    },
  });
}

// Marketplace token value calculator component, using the same pattern as BalanceList
const MarketplaceTokenValue = ({ token, publicKey, onValueCalculated, onBalanceCalculated }: { 
  token: TokenConfig, 
  publicKey: PublicKey,
  onValueCalculated: (symbol: string, value: number) => void,
  onBalanceCalculated: (symbol: string, balance: number) => void
}) => {
  // get balance info for each public key
  const balanceInfo = useBalanceInfo(publicKey);
  
  // console.log(`Balance info for ${token.symbol} at ${publicKey.toString()}:`, balanceInfo);
  
  useEffect(() => {
    // console.log(`MarketplaceTokenValue useEffect for ${token.symbol}:`, { balanceInfo, tokenMint: token.publicKey });
    
    if (!balanceInfo) {
      // console.log(`Still loading balance info for ${token.symbol}`);
      return;
    }
    
    // Check if this balance info is for our marketplace token
    const marketplaceTokenMint = new PublicKey(token.publicKey);
    const balanceMint = balanceInfo.mint;
    
    if (!balanceMint || !balanceMint.equals(marketplaceTokenMint)) {
      console.log(`Balance info mint doesn't match marketplace token mint for ${token.symbol}`, {
        balanceMint: balanceMint?.toString(),
        marketplaceTokenMint: marketplaceTokenMint.toString()
      });
      // This public key doesn't have this marketplace token, set to 0
      onValueCalculated(token.symbol, 0);
      onBalanceCalculated(token.symbol, 0);
      return;
    }
    
    // Calculate balance from the balanceInfo (same as BalanceListItem)
    const { amount, decimals } = balanceInfo;
    const balance = amount / Math.pow(10, decimals);
    
    console.log(`${token.symbol} balance for ${publicKey.toString()}:`, balance, 'amount:', amount, 'decimals:', decimals);
    
    // Convert to USDT value: balance / exchangeRate
    const valueInUsdt = token.exchangeRate > 0 ? balance / token.exchangeRate : 0;
    console.log(`${token.symbol} value in USDT for ${publicKey.toString()}:`, valueInUsdt);
    
    // Call callbacks with the values for this specific public key
    onValueCalculated(`${token.symbol}-${publicKey.toString()}`, valueInUsdt);
    onBalanceCalculated(`${token.symbol}-${publicKey.toString()}`, balance);
    
  }, [balanceInfo, token.symbol, token.exchangeRate, token.publicKey, publicKey, onValueCalculated, onBalanceCalculated]);
  
  return null; // This component doesn't render anything
};

// wDOMI is priced like native DOMI, not through the marketplace exchangeRate,
// so wrapping/unwrapping leaves the total account value unchanged.
const WrappedDomiBalance = ({ publicKey, onBalanceCalculated }: {
  publicKey: PublicKey,
  onBalanceCalculated: (key: string, balance: number) => void
}) => {
  const balanceInfo = useBalanceInfo(publicKey);

  useEffect(() => {
    if (!balanceInfo) {
      return;
    }

    const isWdomi = balanceInfo.mint?.equals(WRAPPED_DOMI_MINT);
    const balance = isWdomi
      ? balanceInfo.amount / Math.pow(10, balanceInfo.decimals)
      : 0;

    onBalanceCalculated(publicKey.toString(), balance);
  }, [balanceInfo, publicKey, onBalanceCalculated]);

  return null;
};

export default function TotalAccountValue({ wallet }: TotalAccountValueProps): JSX.Element | null {
  const classes = useStyles();
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [marketplaceTokenValues, setMarketplaceTokenValues] = useState<Record<string, number>>({});
  const [marketplaceTokenBalances, setMarketplaceTokenBalances] = useState<Record<string, number>>({});
  const [wdomiBalances, setWdomiBalances] = useState<Record<string, number>>({});

  // Get wallet public keys
  const [publicKeys, loaded] = useWalletPublicKeys();
  
  // Token data hooks. wUSDT lives under the main token program and is a
  // separate asset line from legacy USDT.
  const { data: usdtTokenAccounts } = useTokenAccounts(USDT_PROGRAM_ID);
  const { data: mainTokenAccounts } = useTokenAccounts(TOKEN_PROGRAM_ID);
  const { data: btcTokenAccounts } = useTokenAccounts(BTCI_PROGRAM_ID);
  const domiBalanceInfo = useBalanceInfo(wallet?.publicKey);
  const {
    data: domiPrice,
    isLoading: isDomiPriceLoading,
    error: domiPriceError,
    dataUpdatedAt: domiPriceUpdatedAt,
  } = useDomiPrice();
  const {
    data: btcPrice,
    isLoading: isBtcPriceLoading,
    error: btcPriceError,
    dataUpdatedAt: btcPriceUpdatedAt,
  } = useBtcPrice();

  // Fetch marketplace tokens
  const {
    data: marketplaceTokens = [],
    isLoading: isMarketplaceTokensLoading,
    error: marketplaceTokensError,
    dataUpdatedAt: marketplaceTokensUpdatedAt,
  } = useQuery({
    queryKey: ['supportedTokens'],
    queryFn: fetchSupportedTokens,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Keep retrying so we recover once the network is back
    cacheTime: Infinity,
    retry: 2,
    meta: {
      persist: true,
    },
    onError: (error: Error) => {
      console.error(`Failed to load marketplace tokens: ${error.message}`);
    }
  });

  // Re-evaluate staleness periodically so the "as of" indicator appears/updates
  // even when nothing else triggers a render.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(timer);
  }, []);

  // Callback for marketplace token value updates
  const handleMarketplaceTokenValue = useCallback((key: string, value: number) => {
    console.log(`Setting marketplace token value: ${key} = ${value}`);
    setMarketplaceTokenValues(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Callback for marketplace token balance updates
  const handleMarketplaceTokenBalance = useCallback((key: string, balance: number) => {
    console.log(`Setting marketplace token balance: ${key} = ${balance}`);
    setMarketplaceTokenBalances(prev => ({
      ...prev,
      [key]: balance
    }));
  }, []);

  const handleWdomiBalance = useCallback((key: string, balance: number) => {
    setWdomiBalances(prev => ({ ...prev, [key]: balance }));
  }, []);

  // Total wDOMI across live accounts only, so a closed account (full unwrap)
  // doesn't leave a stale balance in the total.
  const wdomiBalance = useMemo(() => {
    const liveKeys = new Set(publicKeys.map(pk => pk.toString()));
    return Object.entries(wdomiBalances).reduce(
      (sum, [key, balance]) => (liveKeys.has(key) ? sum + balance : sum),
      0,
    );
  }, [wdomiBalances, publicKeys]);

  // Aggregate marketplace token values by symbol
  const aggregatedMarketplaceValues = useMemo(() => {
    const result: Record<string, number> = {};
    Object.entries(marketplaceTokenValues).forEach(([key, value]) => {
      const [symbol] = key.split('-');
      result[symbol] = (result[symbol] || 0) + value;
    });
    console.log('Aggregated marketplace values:', result);
    return result;
  }, [marketplaceTokenValues]);

  // Aggregate marketplace token balances by symbol
  const aggregatedMarketplaceBalances = useMemo(() => {
    const result: Record<string, number> = {};
    Object.entries(marketplaceTokenBalances).forEach(([key, balance]) => {
      const [symbol] = key.split('-');
      result[symbol] = (result[symbol] || 0) + balance;
    });
    console.log('Aggregated marketplace balances:', result);
    return result;
  }, [marketplaceTokenBalances]);

  // Calculate all balances including marketplace tokens
  const balances = useMemo(() => {
    if (!wallet?.publicKey) {
      return { usdt: 0, wusdt: 0, btc: 0, domi: 0, wdomi: 0, marketplace: marketplaceTokenValues };
    }

    // USDT balance
    const usdtBalance = (usdtTokenAccounts ?? []).reduce((acc, curr) => {
      try {
        const tokenAmount = curr?.account?.data?.parsed?.info?.tokenAmount;
        return acc + (tokenAmount?.uiAmount || 0);
      } catch (error) {
        console.warn('Error calculating USDT balance:', error);
        return acc;
      }
    }, 0);

    // wUSDT balance (main token program, single mint)
    const wusdtMintAddress = WUSDT_MINT?.toBase58();
    const wusdtBalance = wusdtMintAddress
      ? (mainTokenAccounts ?? []).reduce((acc, curr) => {
          try {
            const info = curr?.account?.data?.parsed?.info;
            if (info?.mint !== wusdtMintAddress) {
              return acc;
            }
            return acc + (info?.tokenAmount?.uiAmount || 0);
          } catch (error) {
            console.warn('Error calculating wUSDT balance:', error);
            return acc;
          }
        }, 0)
      : 0;

    // BTC balance
    const btcBalance = (btcTokenAccounts ?? []).reduce((acc, curr) => {
      try {
        const tokenAmount = curr?.account?.data?.parsed?.info?.tokenAmount;
        return acc + (tokenAmount?.uiAmount || 0);
      } catch (error) {
        console.warn('Error calculating BTC balance:', error);
        return acc;
      }
    }, 0);

    // DOMI balance (convert from lamports to DOMI)
    let domiBalance = 0;
    try {
      if (domiBalanceInfo?.amount && domiBalanceInfo?.decimals) {
        domiBalance = domiBalanceInfo.amount / Math.pow(10, domiBalanceInfo.decimals);
      }
    } catch (error) {
      console.warn('Error calculating DOMI balance:', error);
    }

    return {
      usdt: usdtBalance,
      wusdt: wusdtBalance,
      btc: btcBalance,
      domi: domiBalance,
      wdomi: wdomiBalance,
      marketplace: aggregatedMarketplaceValues,
    };
  }, [wallet?.publicKey, usdtTokenAccounts, mainTokenAccounts, btcTokenAccounts, domiBalanceInfo, wdomiBalance, aggregatedMarketplaceValues]);

  // When the pricing backend is unreachable we keep rendering the total from the last persisted prices. 
  const staleInfo = useMemo(() => {
    const domiPriceInUse = balances.domi > 0 || balances.wdomi > 0;
    const btcPriceInUse = balances.btc > 0;
    const marketplaceRatesInUse =
      marketplaceTokensUpdatedAt > 0 &&
      Object.values(aggregatedMarketplaceBalances).some((balance) => balance > 0);

    const updatedAts: number[] = [];
    if (domiPriceInUse && typeof domiPrice === 'number' && domiPriceUpdatedAt > 0) {
      updatedAts.push(domiPriceUpdatedAt);
    }
    if (btcPriceInUse && typeof btcPrice === 'number' && btcPriceUpdatedAt > 0) {
      updatedAts.push(btcPriceUpdatedAt);
    }
    if (marketplaceRatesInUse) {
      updatedAts.push(marketplaceTokensUpdatedAt);
    }

    if (updatedAts.length === 0) {
      return { isStale: false, lastUpdatedAt: 0 };
    }

    // The total is only as fresh as its oldest ingredient.
    const lastUpdatedAt = Math.min(...updatedAts);
    const fetchFailed =
      (domiPriceInUse && Boolean(domiPriceError)) ||
      (btcPriceInUse && Boolean(btcPriceError)) ||
      (marketplaceRatesInUse && Boolean(marketplaceTokensError));
    const isStale = fetchFailed || now - lastUpdatedAt > PRICE_STALE_AFTER_MS;

    return { isStale, lastUpdatedAt };
  }, [
    balances.domi,
    balances.wdomi,
    balances.btc,
    aggregatedMarketplaceBalances,
    domiPrice,
    domiPriceUpdatedAt,
    domiPriceError,
    btcPrice,
    btcPriceUpdatedAt,
    btcPriceError,
    marketplaceTokensUpdatedAt,
    marketplaceTokensError,
    now,
  ]);

  // Calculate total value in USDT
  const totalValueData = useMemo(() => {
    try {
      const safeDomiPrice = typeof domiPrice === 'number' ? domiPrice : 0;
      const safeBtcPrice = typeof btcPrice === 'number' ? btcPrice : 0;

      const usdtValue = balances.usdt;
      // wUSDT is dollar-pegged like USDT; no external price needed.
      const wusdtValue = balances.wusdt;
      const btcValue = balances.btc * safeBtcPrice;
      const domiValue = balances.domi * safeDomiPrice;
      // wDOMI carries the same USDT price as native DOMI
      const wdomiValue = balances.wdomi * safeDomiPrice;

      // Calculate marketplace token values (already in USDT)
      const marketplaceTokensValue = Object.values(balances.marketplace).reduce((sum, value) => sum + value, 0);

      console.log('Total value calculation:', {
        usdtValue,
        btcValue,
        domiValue,
        wdomiValue,
        marketplaceTokensValue,
        marketplaceTokenValues: balances.marketplace
      });

      const total = usdtValue + wusdtValue + btcValue + domiValue + wdomiValue + marketplaceTokensValue;

      return {
        total: isNaN(total) ? 0 : total,
        breakdown: {
          usdt: isNaN(usdtValue) ? 0 : usdtValue,
          wUSDT: isNaN(wusdtValue) ? 0 : wusdtValue,
          btc: isNaN(btcValue) ? 0 : btcValue,
          domi: isNaN(domiValue) ? 0 : domiValue,
          wDOMI: isNaN(wdomiValue) ? 0 : wdomiValue,
          ...balances.marketplace,
        },
        pricesAvailable: {
          domi: !!domiPrice,
          btc: !!btcPrice,
        },
      };
    } catch (error) {
      console.warn('Error calculating total value:', error);
      return {
        total: 0,
        breakdown: { usdt: 0, btc: 0, domi: 0 },
        pricesAvailable: { domi: false, btc: false },
      };
    }
  }, [balances, domiPrice, btcPrice]);

  // Get assets with non-zero balances for breakdown
  const nonZeroAssets = useMemo(() => {
    try {
      const assets: Asset[] = [];
      
      if (balances.usdt > 0) {
        assets.push({
          name: 'USDT',
          balance: balances.usdt,
          value: balances.usdt,
          priceAvailable: true,
        });
      }

      if (balances.wusdt > 0) {
        assets.push({
          name: 'wUSDT',
          balance: balances.wusdt,
          value: balances.wusdt,
          priceAvailable: true,
        });
      }

      if (balances.btc > 0) {
        assets.push({
          name: 'BTC',
          balance: balances.btc,
          value: balances.btc * (btcPrice || 0),
          priceAvailable: !!btcPrice,
        });
      }
      
      if (balances.domi > 0) {
        assets.push({
          name: 'DOMI',
          balance: balances.domi,
          value: balances.domi * (domiPrice || 0),
          priceAvailable: !!domiPrice,
        });
      }

      if (balances.wdomi > 0) {
        assets.push({
          name: 'wDOMI',
          balance: balances.wdomi,
          value: balances.wdomi * (domiPrice || 0),
          priceAvailable: !!domiPrice,
        });
      }

      // Add marketplace tokens with non-zero balances
      Object.entries(balances.marketplace).forEach(([symbol, value]) => {
        console.log(`Processing marketplace token ${symbol}:`, { value, balance: aggregatedMarketplaceBalances[symbol] });
        if (value > 0) {
          const token = marketplaceTokens.find(t => t.symbol === symbol);
          const actualBalance = aggregatedMarketplaceBalances[symbol] || 0;
          
          console.log(`Token ${symbol} found:`, token, 'actualBalance:', actualBalance);
          
          if (token && actualBalance > 0) {
            assets.push({
              name: symbol,
              balance: actualBalance,
              value: value, // Already in USDT
              priceAvailable: true,
            });
          }
        }
      });
      
      console.log('Final nonZeroAssets:', assets);
      
      return assets;
    } catch (error) {
      console.warn('Error calculating non-zero assets:', error);
      return [];
    }
  }, [balances, btcPrice, domiPrice, marketplaceTokens, aggregatedMarketplaceBalances]);

  // Early return after all hooks have been called
  if (!wallet?.publicKey || !loaded) {
    return null;
  }

  // Check if data is loaded
  const hasNonZeroBalances = balances.usdt > 0 || balances.wusdt > 0 || balances.btc > 0 || balances.domi > 0 || balances.wdomi > 0 || Object.values(aggregatedMarketplaceValues).some(value => value > 0);
  const isLoading = (isDomiPriceLoading || isBtcPriceLoading || !domiBalanceInfo || isMarketplaceTokensLoading) && hasNonZeroBalances;
  
  // Error state 
  const missingDomiPrice =
    Boolean(domiPriceError) &&
    domiPrice === undefined &&
    (balances.domi > 0 || balances.wdomi > 0);
  const missingBtcPrice =
    Boolean(btcPriceError) && btcPrice === undefined && balances.btc > 0;
  const hasError = (missingDomiPrice || missingBtcPrice) && hasNonZeroBalances;

  if (isLoading) {
    // While prices/balances are still loading (or the network is unreachable)
    // render nothing at all — no spinner that could sit there indefinitely.
    return null;
  }

  if (hasError) {
    return (
      <Box className={classes.container}>
        <Typography variant="body2" color="error">
          Unable to fetch current prices
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
          Showing balances without USD values
        </Typography>
      </Box>
    );
  }

  const formatCurrency = (value: number): string => {
    try {
      const numValue = typeof value === 'number' ? value : parseFloat(value as string) || 0;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numValue);
    } catch (error) {
      console.warn('Error formatting currency:', error);
      return '$0.00';
    }
  };

  const formatBalance = (value: number, symbol: string): string | null => {
    try {
      if (!value || value === 0) return null;
      
      const numValue = typeof value === 'number' ? value : parseFloat(value as string) || 0;
      const formattedValue = numValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: symbol === 'BTC' ? 8 : 4,
      });
      
      return `${formattedValue} ${symbol}`;
    } catch (error) {
      console.warn('Error formatting balance:', error);
      return `0 ${symbol}`;
    }
  };

  const formatAsOfTime = (timestamp: number): string => {
    try {
      const date = new Date(timestamp);
      const isSameDay = date.toDateString() === new Date().toDateString();
      if (isSameDay) {
        return date.toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: '2-digit',
        });
      }
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch (error) {
      console.warn('Error formatting as-of time:', error);
      return '';
    }
  };

  try {
    return (
      <Box className={classes.container}>
        {loaded && marketplaceTokens.length > 0 && marketplaceTokens
          // wUSDT already has its own asset line; a marketplace listing of
          // the same mint must never double-count it.
          .filter(token =>
            token.publicKey !== WRAPPED_DOMI_MINT.toBase58() &&
            token.publicKey !== WUSDT_MINT?.toBase58())
          .map(token =>
          publicKeys.map(publicKey => {
            const key = `${token.symbol}-${publicKey.toString()}`;
            console.log('Rendering MarketplaceTokenValue for:', token.symbol, publicKey.toString());
            return (
              <MarketplaceTokenValue
                key={key}
                token={token}
                publicKey={publicKey}
                onValueCalculated={handleMarketplaceTokenValue}
                onBalanceCalculated={handleMarketplaceTokenBalance}
              />
            );
          })
        ).flat()}

        {loaded && publicKeys.map(publicKey => (
          <WrappedDomiBalance
            key={`wdomi-${publicKey.toString()}`}
            publicKey={publicKey}
            onBalanceCalculated={handleWdomiBalance}
          />
        ))}

        <Typography variant="h6" className={classes.totalValue}>
          Total Account Value
        </Typography>
        <Typography variant="h4" className={classes.totalValue}>
          {formatCurrency(totalValueData?.total || 0)}
        </Typography>
        {staleInfo.isStale && staleInfo.lastUpdatedAt > 0 && (
          <Tooltip
            title="Live prices are unavailable right now — showing the last saved value."
            placement="bottom"
            arrow
          >
            <div className={classes.staleIndicator}>
              <ScheduleIcon className={classes.staleIcon} />
              <Typography variant="caption" color="textSecondary">
                As of {formatAsOfTime(staleInfo.lastUpdatedAt)}
              </Typography>
            </div>
          </Tooltip>
        )}
        {nonZeroAssets.length > 0 && (
          <>
            <div className={classes.expandButton} onClick={() => setShowBreakdown(!showBreakdown)}>
              <Typography variant="caption" color="textSecondary" style={{ marginRight: 4 }}>
                {showBreakdown ? 'Hide' : 'Show'} breakdown
              </Typography>
              {showBreakdown ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </div>
            
            <Collapse in={showBreakdown} timeout="auto" unmountOnExit>
              <div className={classes.breakdown}>
                {nonZeroAssets.map((asset: Asset) => (
                  <Typography key={asset.name} variant="body2" className={classes.breakdownItem}>
                    {formatBalance(asset.balance, asset.name)} = {
                      asset.priceAvailable 
                        ? formatCurrency(asset.value)
                        : 'Price unavailable'
                    }
                  </Typography>
                ))}
              </div>
            </Collapse>
          </>
        )}
      </Box>
    );
  } catch (error) {
    console.error('Error rendering TotalAccountValue:', error);
    return (
      <Box className={classes.container}>
        <Typography variant="body2" color="error">
          Unable to load account value
        </Typography>
      </Box>
    );
  }
}